/**
 * Builds a combined flow graph for an eval scenario run.
 *
 * The graph mixes the *real* agent pipeline (the same Compliance → Routing →
 * Agent Execution → Response nodes used by triggers and user activity, reusing
 * the shared slack flow node configs / dialog builders) with eval-specific
 * step nodes (interact turns without a linked log, assert checks, and the final
 * run outcome). Each interact turn becomes its own row, mirroring how the
 * shared FlowLogView explodes multi-turn conversations.
 */

import { Edge, MarkerType, Node, Position } from '@xyflow/react';
import {
  CheckCircle2,
  FlaskConical,
  MessageSquare,
  Paperclip,
  ShieldCheck,
  Sparkles,
  Timer,
  Wrench,
  XCircle,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

import type { ObservabilityLogsRetrieve200ResultsItem } from '@/gen-api/models';
import { slackFlowWarnProceed } from '@/components/flow/configs/slackFlow';
import type { DialogField, FlowNodeConfig } from '@/components/flow/types';
import { getIncludedNodeIds, resolveFlowEdgeColor } from '@/components/flow/utils/flowBuilder';
import { extractItemMetadata, type MetaDoc } from '@/components/flow/utils/metadata';
import type { ScenarioRunDetail } from '@/components/eval-studio/evalStudioApi';
import {
  formatScenarioCheckDisplay,
  formatScenarioExpectedValue,
  formatScenarioInteractionDetail,
  formatScenarioInteractionLabel,
  formatScenarioInteractionSubtitle,
  formatScenarioOperator,
  formatScenarioScope,
  formatCompactDuration,
  resolveAgentDisplayName,
  type AgentNameById,
} from '@/components/eval-studio/scenarioStepFormatting';
import type { ScenarioRunSummary, ScenarioRunTrace } from '@/types/eval-studio';

export type ScenarioFlowGraph = {
  nodes: Node[];
  edges: Edge[];
  dialogs: Map<string, { title: string; fields: DialogField[] }>;
};

type BuiltNode = {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  fields: DialogField[];
  edgeColor?: string;
};

const COL_GAP = 300;

const slackNodeById = new Map<string, FlowNodeConfig>(slackFlowWarnProceed.nodes.map((node) => [node.id, node]));

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function humanizeToken(value: string) {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function preField(value: string) {
  return (
    <pre className="max-h-[250px] overflow-auto whitespace-pre-wrap rounded-md border bg-gray-50 p-3 text-xs dark:bg-gray-900">
      {value}
    </pre>
  );
}

function markdownField(value: string) {
  return (
    <div className="prose prose-sm dark:prose-invert max-h-[250px] max-w-md overflow-y-auto rounded-md border bg-gray-50 p-3 dark:bg-gray-800">
      <ReactMarkdown>{value}</ReactMarkdown>
    </div>
  );
}

const COMPLIANCE_ACTION_LABELS: Record<string, string> = {
  accept_changes: 'Accept Changes',
  edit_message: 'Edit Message',
  override_submit: 'Proceed with Original',
};

/** Format resource_violations ({ruleset: rule | rule[]}) into readable lines. */
function formatComplianceViolations(violations: unknown): string {
  if (!isRecord(violations)) return '';
  const lines: string[] = [];
  for (const [ruleset, rules] of Object.entries(violations)) {
    const value = Array.isArray(rules) ? rules.map((r) => String(r)).join(', ') : String(rules);
    lines.push(value ? `${ruleset}: ${value}` : ruleset);
  }
  return lines.join('\n');
}

/**
 * Compliance-officer detail fields distilled by the backend from the pipeline's
 * ``filter_result`` (reason, proposed amendment, violations) plus the follow-up
 * actions a user could take — mirroring Slack's compliance message.
 */
function complianceDetailFields(payload: Record<string, unknown>): DialogField[] {
  const fields: DialogField[] = [];
  if (payload.compliance_reason) {
    fields.push({ label: 'Reason', value: String(payload.compliance_reason) });
  }
  if (payload.compliance_amendment) {
    fields.push({ label: 'Suggested amendment', value: markdownField(String(payload.compliance_amendment)) });
  }
  const violations = formatComplianceViolations(payload.compliance_violations);
  if (violations) {
    fields.push({ label: 'Resource violations', value: preField(violations) });
  }
  if (Array.isArray(payload.compliance_actions) && payload.compliance_actions.length > 0) {
    const labels = payload.compliance_actions.map(
      (a) => COMPLIANCE_ACTION_LABELS[String(a)] ?? humanizeToken(String(a))
    );
    fields.push({ label: 'Available actions', value: labels.join(', ') });
  }
  return fields;
}

type TurnToolCall = { name: string; mocked: boolean };

/** Parse a turn's ``tool_calls`` into a compact {name, mocked} list. */
function parseTurnToolCalls(payload: Record<string, unknown>): TurnToolCall[] {
  const raw = Array.isArray(payload.tool_calls) ? payload.tool_calls : [];
  const calls: TurnToolCall[] = [];
  for (const entry of raw) {
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      const record = entry as Record<string, unknown>;
      if (record.name) {
        calls.push({ name: String(record.name), mocked: record.mocked === true });
      }
    }
  }
  return calls;
}

function toolCallFields(calls: TurnToolCall[]): DialogField[] {
  if (!calls.length) return [];
  const called = calls.map((c) => c.name).join(', ');
  const mocked = calls.filter((c) => c.mocked).map((c) => c.name);
  const fields: DialogField[] = [{ label: 'Tools called', value: called }];
  if (mocked.length) {
    fields.push({
      label: 'Mocked (Eval Studio)',
      value: `${mocked.join(', ')} — simulated result, the real tool was not executed (no side effects).`,
    });
  }
  return fields;
}

function statusColor(status?: string) {
  if (status === 'passed') return '#10b981';
  if (status === 'soft_pass') return '#f97316';
  if (status === 'failed' || status === 'error') return '#ef4444';
  if (status === 'skipped' || status === 'cancelled') return '#9ca3af';
  return '#f59e0b';
}

function pipelineNodeColor(id: string): string {
  switch (id) {
    case 'n2':
      return '#f59e0b';
    case 'n4':
      return '#8b5cf6';
    case 'n6':
      return '#10b981';
    case 'n7':
      return '#0ea5e9';
    default:
      return '#6366f1';
  }
}

function evalInteractEntryFields(payload: Record<string, unknown>, turnIndex: number): DialogField[] {
  return turnEntryFields(payload, turnIndex);
}

// ---------------------------------------------------------------------------
// Eval-specific node builders
// ---------------------------------------------------------------------------

function turnEntryFields(payload: Record<string, unknown>, turnIndex: number): DialogField[] {
  const fields: DialogField[] = [{ label: 'Turn', value: String(turnIndex + 1) }];
  const interactType = payload.interact_type || payload.type;
  const interactionDetail = formatScenarioInteractionDetail(payload);

  if (payload.step_id) fields.push({ label: 'Step ID', value: String(payload.step_id) });
  if (interactType) {
    fields.push({ label: 'Interaction', value: formatScenarioInteractionLabel(String(interactType)) });
  }
  if (interactionDetail && !payload.sent_text) fields.push({ label: 'Details', value: interactionDetail });
  if (payload.sent_text) fields.push({ label: 'Sent message', value: markdownField(String(payload.sent_text)) });
  return fields;
}

function fallbackTurnNodes(
  payload: Record<string, unknown>,
  turnIndex: number,
  agentNames?: AgentNameById | null
): BuiltNode[] {
  const interactType = String(payload.interact_type || payload.type || 'user_message');
  const cancelled = Boolean(payload.cancelled);
  const responseText = typeof payload.response_text === 'string' ? payload.response_text : '';
  // Eval turns are not linked to a full observability log, so reconstruct which
  // pipeline stages actually ran from the captured progress stream. A superseded
  // turn still runs compliance/routing before being cancelled — surfacing those
  // stages keeps the graph honest instead of looking like the message never ran.
  const progressTypes: string[] = Array.isArray(payload.progress_event_types)
    ? payload.progress_event_types.map((t) => String(t))
    : [];
  const ranCompliance = progressTypes.some((t) => t.startsWith('filtering')) || Boolean(payload.compliance_action);
  const ranRouting = progressTypes.some((t) => t.startsWith('routing'));

  const title = formatScenarioInteractionLabel(interactType);
  const subtitle = formatScenarioInteractionSubtitle(interactType);
  let icon: React.ReactNode = <MessageSquare size={18} color="#6366f1" />;
  if (interactType === 'delay') {
    icon = <Timer size={18} color="#6366f1" />;
  } else if (interactType === 'file_upload') {
    icon = <Paperclip size={18} color="#6366f1" />;
  } else if (
    interactType === 'compliance_accept' ||
    interactType === 'compliance_edit' ||
    interactType === 'compliance_bypass'
  ) {
    icon = <ShieldCheck size={18} color="#f59e0b" />;
  }

  const nodes: BuiltNode[] = [
    {
      id: `turn-${turnIndex}-entry`,
      title,
      subtitle,
      icon,
      color: '#6366f1',
      fields: turnEntryFields(payload, turnIndex),
    },
  ];

  // Compliance stage (n2-equivalent) — reconstructed from the progress stream.
  if (ranCompliance) {
    const complianceAction = payload.compliance_action ? String(payload.compliance_action) : null;
    nodes.push({
      id: `turn-${turnIndex}-compliance`,
      title: 'Compliance',
      subtitle: complianceAction ?? (cancelled ? 'Checked (superseded)' : 'Checked'),
      icon: <ShieldCheck size={18} color="#f59e0b" />,
      color: '#f59e0b',
      fields: [
        { label: 'Compliance action', value: complianceAction ?? 'Ran (no final decision — turn superseded)' },
        ...complianceDetailFields(payload),
      ],
    });
  }

  // Routing stage (n4-equivalent).
  if (ranRouting) {
    const routed = progressTypes.includes('routing.completed');
    nodes.push({
      id: `turn-${turnIndex}-routing`,
      title: 'Routing',
      subtitle: routed ? 'Routed' : cancelled ? 'Superseded during routing' : 'Routing',
      icon: <Sparkles size={18} color="#8b5cf6" />,
      color: '#8b5cf6',
      fields: [
        { label: 'Routing', value: routed ? 'Completed' : 'Started, not completed' },
        ...(payload.agent_name || payload.agent_id
          ? [
              {
                label: 'Agent',
                value: resolveAgentDisplayName(String(payload.agent_name || payload.agent_id), agentNames),
              },
            ]
          : []),
      ],
    });
  }

  // Tools stage — the tool calls the agent made this turn, flagging any that
  // Eval Studio mocked (MCP / write tools intercepted to avoid side effects).
  const toolCalls = parseTurnToolCalls(payload);
  if (toolCalls.length) {
    const anyMocked = toolCalls.some((call) => call.mocked);
    nodes.push({
      id: `turn-${turnIndex}-tools`,
      title: 'Tools',
      subtitle: anyMocked ? 'Mocked (Eval Studio)' : `${toolCalls.length} call${toolCalls.length === 1 ? '' : 's'}`,
      icon: anyMocked ? <FlaskConical size={18} color="#8b5cf6" /> : <Wrench size={18} color="#6366f1" />,
      color: anyMocked ? '#8b5cf6' : '#6366f1',
      fields: toolCallFields(toolCalls),
    });
  }

  if (responseText || payload.agent_name || payload.compliance_action) {
    const responseFields: DialogField[] = [];
    if (payload.agent_name || payload.agent_id) {
      responseFields.push({
        label: 'Agent',
        value: resolveAgentDisplayName(String(payload.agent_name || payload.agent_id), agentNames),
      });
    }
    if (payload.compliance_action) {
      responseFields.push({ label: 'Compliance action', value: String(payload.compliance_action) });
    }
    if (payload.handover_initiated) {
      responseFields.push({
        label: 'Handover',
        value: payload.handover_message ? String(payload.handover_message) : 'Yes',
      });
    }
    if (responseText) responseFields.push({ label: 'Agent response', value: markdownField(responseText) });
    if (cancelled) responseFields.push({ label: 'Cancelled', value: 'Yes' });

    nodes.push({
      id: `turn-${turnIndex}-response`,
      title: 'Agent response',
      subtitle: cancelled ? 'No response' : 'LLM reply',
      icon: <Sparkles size={18} color={cancelled ? '#ef4444' : '#10b981'} />,
      color: cancelled ? '#ef4444' : '#10b981',
      fields: responseFields,
    });
  } else if (cancelled) {
    // Superseded before producing any reply (Slack-style debounce): show an
    // explicit terminal node so the turn does not look like it never started.
    nodes.push({
      id: `turn-${turnIndex}-superseded`,
      title: 'Superseded',
      subtitle: 'No reply — newer message took over',
      icon: <XCircle size={18} color="#ef4444" />,
      color: '#ef4444',
      fields: [
        {
          label: 'Outcome',
          value: 'Cancelled by debounce: a newer message superseded this turn before it replied.',
        },
      ],
    });
  }

  return nodes;
}

function pipelineTurnNodes(
  item: ObservabilityLogsRetrieve200ResultsItem,
  payload: Record<string, unknown>,
  turnIndex: number,
  _agentNames?: AgentNameById | null
): BuiltNode[] {
  const md = extractItemMetadata(item) as MetaDoc;
  const ids = getIncludedNodeIds(item, slackFlowWarnProceed.nodes);
  const interactType = String(payload.interact_type || payload.type || 'user_message');
  const entryTitle = formatScenarioInteractionLabel(interactType);
  const entrySubtitle = formatScenarioInteractionSubtitle(interactType);

  return ids.map((nid, index) => {
    const config = slackNodeById.get(nid);
    const isEntry = nid === 'n1';
    const extra = isEntry ? evalInteractEntryFields(payload, turnIndex) : [];
    const baseFields = config ? config.dialogBuilder(item, md) : [];
    const prevId = index > 0 ? ids[index - 1] : undefined;
    const edgeColor =
      prevId != null ? resolveFlowEdgeColor(prevId, nid, item, slackFlowWarnProceed) : pipelineNodeColor(nid);

    return {
      id: `turn-${turnIndex}-${nid}`,
      title: isEntry ? entryTitle : (config?.title ?? humanizeToken(nid)),
      subtitle: isEntry ? entrySubtitle : (config?.subtitle ?? ''),
      icon: isEntry ? (
        <MessageSquare size={18} color="#6366f1" />
      ) : (
        (config?.icon ?? <Sparkles size={18} color="#6366f1" />)
      ),
      color: edgeColor,
      edgeColor,
      fields: [...extra, ...baseFields],
    };
  });
}

function assertNode(
  payload: Record<string, unknown>,
  assertIndex: number,
  agentNames?: AgentNameById | null
): BuiltNode {
  const status = String(payload.status || 'unknown');
  const failed = status === 'failed' || status === 'error';
  const checkType = String(payload.check_type || 'assert');
  const operator = payload.operator ? String(payload.operator) : undefined;
  const checkLabel = formatScenarioCheckDisplay(
    {
      check_type: checkType,
      operator,
      expected: payload.expected,
    },
    agentNames
  );
  const expectedSummary = formatScenarioExpectedValue(
    {
      check_type: checkType,
      expected: payload.expected,
    },
    agentNames
  );

  const fields: DialogField[] = [
    { label: 'Check', value: checkLabel },
    { label: 'Status', value: humanizeToken(status) },
  ];
  if (operator) fields.push({ label: 'Operator', value: formatScenarioOperator(operator) ?? operator });
  if (payload.scope) fields.push({ label: 'Scope', value: formatScenarioScope(String(payload.scope)) ?? '' });
  if (payload.message) fields.push({ label: 'Message', value: preField(String(payload.message)) });
  if (expectedSummary) fields.push({ label: 'Expected', value: expectedSummary });
  if (payload.expected != null) {
    fields.push({ label: 'Expected details', value: preField(JSON.stringify(payload.expected, null, 2)) });
  }
  if (payload.actual != null) {
    fields.push({ label: 'Actual', value: preField(JSON.stringify(payload.actual, null, 2)) });
  }

  return {
    id: `assert-${assertIndex}`,
    title: checkLabel,
    subtitle: failed ? 'Assertion failed' : 'Assertion passed',
    icon: failed ? <XCircle size={18} color="#ef4444" /> : <CheckCircle2 size={18} color="#10b981" />,
    color: statusColor(status),
    fields,
  };
}

function outcomeNode(run: ScenarioRunSummary, detail?: ScenarioRunDetail | null): BuiltNode {
  const failed = run.status === 'failed' || run.status === 'error';
  const softPass = run.status === 'soft_pass';
  const fields: DialogField[] = [{ label: 'Final status', value: humanizeToken(run.status) }];
  if (run.duration_ms != null) fields.push({ label: 'Duration', value: formatCompactDuration(run.duration_ms) });
  if (detail?.error_message) fields.push({ label: 'Error', value: preField(detail.error_message) });

  return {
    id: 'outcome',
    title: failed ? 'Run failed' : run.status === 'passed' ? 'Run passed' : softPass ? 'Run soft pass' : 'Run finished',
    subtitle: humanizeToken(run.status),
    icon: failed ? <XCircle size={18} color="#ef4444" /> : <ShieldCheck size={18} color={statusColor(run.status)} />,
    color: statusColor(run.status),
    fields,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getScenarioRunObservabilityLogIds(trace?: ScenarioRunTrace | null): string[] {
  if (!trace?.events?.length) return [];
  const ids = new Set<string>();
  for (const event of trace.events) {
    if (event.event_type !== 'interact_turn') continue;
    const payload = isRecord(event.payload) ? event.payload : {};
    const logId = payload.observability_log_id;
    if (typeof logId === 'string' && logId) {
      ids.add(logId);
    }
  }
  return Array.from(ids);
}

/**
 * One interact turn plus the assert checks that ran immediately after it.
 *
 * A turn belongs to a *lane* (its conversation track): the main conversation,
 * a parallel conversation (different track), or a thread (reply anchored to a
 * previous turn). The flow chart renders one horizontal lane per conversation
 * and stacks lanes vertically so parallel/threaded chats read clearly.
 */
type TurnGroup = {
  laneKey: string;
  trackId: string;
  threadId: string | null;
  parentStepId: string | null;
  stepId: string | null;
  /** Pipeline nodes for this turn (full pipeline when linked, else fallback). */
  nodes: BuiltNode[];
  /** Assert chips that evaluated right after this turn. */
  checks: BuiltNode[];
};

function turnGroupFromEvent(
  payload: Record<string, unknown>,
  logsById: Map<string, ObservabilityLogsRetrieve200ResultsItem>,
  turnIndex: number,
  agentNames?: AgentNameById | null
): TurnGroup {
  const trackId = String(payload.track_id || 'main').trim() || 'main';
  const threadId = payload.thread_id ? String(payload.thread_id) : null;
  const parentStepId = payload.parent_step_id ? String(payload.parent_step_id) : null;
  const stepId = payload.step_id ? String(payload.step_id) : null;
  const laneKey = threadId ? `${trackId}#thread:${threadId}` : trackId;

  const logId = typeof payload.observability_log_id === 'string' ? payload.observability_log_id : '';
  const item = logId ? logsById.get(logId) : undefined;
  const nodes = item
    ? pipelineTurnNodes(item, payload, turnIndex, agentNames)
    : fallbackTurnNodes(payload, turnIndex, agentNames);

  return { laneKey, trackId, threadId, parentStepId, stepId, nodes, checks: [] };
}

function buildTurnGroups(
  trace: ScenarioRunTrace | null | undefined,
  logsById: Map<string, ObservabilityLogsRetrieve200ResultsItem>,
  detail: ScenarioRunDetail | null | undefined,
  agentNames?: AgentNameById | null
): TurnGroup[] {
  const groups: TurnGroup[] = [];
  let turnIndex = 0;
  let assertIndex = 0;

  const attachCheck = (built: BuiltNode) => {
    const target = groups[groups.length - 1];
    if (target) {
      target.checks.push(built);
    } else {
      // Assert before any interact: anchor it to a main-lane placeholder.
      groups.push({
        laneKey: 'main',
        trackId: 'main',
        threadId: null,
        parentStepId: null,
        stepId: null,
        nodes: [],
        checks: [built],
      });
    }
  };

  if (trace?.events?.length) {
    for (const event of trace.events) {
      const payload = isRecord(event.payload) ? event.payload : {};
      if (event.event_type === 'interact_turn') {
        groups.push(turnGroupFromEvent(payload, logsById, turnIndex, agentNames));
        turnIndex += 1;
      } else if (event.event_type === 'assert_result') {
        attachCheck(assertNode(payload, assertIndex, agentNames));
        assertIndex += 1;
      }
    }
    return groups;
  }

  if (detail?.step_results?.length) {
    detail.step_results.forEach((result, index) => {
      const kind = String(result.kind || '');
      if (kind === 'assert') {
        attachCheck(assertNode(result, assertIndex++, agentNames));
      } else if (kind === 'interact') {
        groups.push({
          laneKey: 'main',
          trackId: 'main',
          threadId: null,
          parentStepId: null,
          stepId: result.step_id ? String(result.step_id) : null,
          nodes: fallbackTurnNodes(
            { step_id: result.step_id, interact_type: result.check_type, sent_text: result.message },
            index,
            agentNames
          ),
          checks: [],
        });
      }
    });
  }

  return groups;
}

function laneHeaderNode(group: TurnGroup): BuiltNode {
  if (group.threadId) {
    return {
      id: `lane-${group.laneKey}`,
      title: 'Thread',
      subtitle: group.parentStepId ? `↳ reply to ${group.parentStepId}` : 'Threaded reply',
      icon: <MessageSquare size={18} color="#8b5cf6" />,
      color: '#8b5cf6',
      fields: [
        { label: 'Lane', value: 'Thread' },
        { label: 'Track', value: group.trackId },
        ...(group.parentStepId ? [{ label: 'Replies to step', value: group.parentStepId }] : []),
      ],
    };
  }
  return {
    id: `lane-${group.laneKey}`,
    title: 'Parallel conversation',
    subtitle: `Track: ${group.trackId}`,
    icon: <MessageSquare size={18} color="#0ea5e9" />,
    color: '#0ea5e9',
    fields: [
      { label: 'Lane', value: 'Parallel conversation' },
      { label: 'Track', value: group.trackId },
    ],
  };
}

/** Order lanes so each track's base lane is followed by its thread lanes. */
function orderedLaneKeys(groups: TurnGroup[]): string[] {
  const trackOrder: string[] = [];
  const lanesByTrack = new Map<string, string[]>();
  for (const g of groups) {
    if (!trackOrder.includes(g.trackId)) trackOrder.push(g.trackId);
    const lanes = lanesByTrack.get(g.trackId) ?? [];
    if (!lanes.includes(g.laneKey)) lanes.push(g.laneKey);
    lanesByTrack.set(g.trackId, lanes);
  }
  // Main track always renders first.
  trackOrder.sort((a, b) => (a === 'main' ? -1 : b === 'main' ? 1 : 0));

  const result: string[] = [];
  for (const track of trackOrder) {
    const lanes = lanesByTrack.get(track) ?? [];
    // Base (non-thread) lane first, then thread lanes in appearance order.
    lanes.sort((a, b) => (a === track ? -1 : b === track ? 1 : 0));
    result.push(...lanes);
  }
  return result;
}

const LANE_GAP = 360;
const CHECK_OFFSET_Y = 150;
const CHECK_GAP_X = 220;

export function buildScenarioRunGraph(
  run: ScenarioRunSummary,
  trace?: ScenarioRunTrace | null,
  logsById?: Map<string, ObservabilityLogsRetrieve200ResultsItem>,
  detail?: ScenarioRunDetail | null,
  agentNames?: AgentNameById | null
): ScenarioFlowGraph {
  const groups = buildTurnGroups(trace, logsById ?? new Map(), detail, agentNames);

  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const dialogs = new Map<string, { title: string; fields: DialogField[] }>();

  const makeMarker = (color: string) => ({
    type: MarkerType.ArrowClosed as const,
    color,
    width: 18,
    height: 18,
  });

  const addNode = (built: BuiltNode, x: number, y: number, orientation: 'horizontal' | 'vertical' = 'horizontal') => {
    nodes.push({
      id: built.id,
      position: { x, y },
      data: { title: built.title, subtitle: built.subtitle, icon: built.icon },
      type: 'appNode',
      sourcePosition: orientation === 'vertical' ? Position.Bottom : Position.Right,
      targetPosition: orientation === 'vertical' ? Position.Top : Position.Left,
    });
    dialogs.set(built.id, { title: built.title, fields: built.fields });
  };

  const addEdge = (
    source: string,
    target: string,
    stroke: string,
    opts: { vertical?: boolean; dashed?: boolean; width?: number } = {}
  ) => {
    edges.push({
      id: `e-${source}-${target}`,
      source,
      target,
      sourceHandle: opts.vertical ? 'bottom' : undefined,
      targetHandle: opts.vertical ? 'top' : undefined,
      style: { stroke, strokeWidth: opts.width ?? 2, ...(opts.dashed ? { strokeDasharray: '5 5' } : {}) },
      markerEnd: makeMarker(stroke),
    });
  };

  const laneKeys = orderedLaneKeys(groups);
  const laneY = new Map<string, number>(laneKeys.map((key, index) => [key, index * LANE_GAP]));
  const laneHeaderId = new Map<string, string>();
  // First lane header is created from the first group seen for that lane.
  const firstGroupForLane = new Map<string, TurnGroup>();
  for (const g of groups) {
    if (!firstGroupForLane.has(g.laneKey)) firstGroupForLane.set(g.laneKey, g);
  }

  // Render a header node per lane at x = 0 (left rail). The main lane gets no
  // synthetic entry node (it previously just repeated the test name); its first
  // real node — the scenario user message (step 0) — becomes the entry point.
  for (const key of laneKeys) {
    if (key === 'main') continue;
    const seed = firstGroupForLane.get(key);
    if (!seed) continue;
    const header = laneHeaderNode(seed);
    laneHeaderId.set(key, header.id);
    addNode(header, 0, laneY.get(key) ?? 0);
  }

  // Track, per lane, the running x cursor and the last node id to chain turns.
  // The main lane starts flush left (no header); other lanes start after theirs.
  const laneCursorX = new Map<string, number>(laneKeys.map((key) => [key, key === 'main' ? 0 : COL_GAP]));
  const laneLastNodeId = new Map<string, string>();
  for (const key of laneKeys) {
    const headerId = laneHeaderId.get(key);
    if (headerId) laneLastNodeId.set(key, headerId);
  }
  // step_id -> the response node id of that turn (for thread branch edges).
  const stepResponseNodeId = new Map<string, string>();

  for (const group of groups) {
    const y = laneY.get(group.laneKey) ?? 0;
    const x = laneCursorX.get(group.laneKey) ?? COL_GAP;
    const prevNodeId = laneLastNodeId.get(group.laneKey);

    if (group.nodes.length === 0) {
      // No turn nodes (e.g. assert before first interact): hang checks off the
      // lane header and keep the cursor where it is.
      const anchorId = prevNodeId ?? laneHeaderId.get(group.laneKey);
      group.checks.forEach((chk, c) => {
        addNode(chk, x + c * CHECK_GAP_X, y + CHECK_OFFSET_Y, 'vertical');
        if (anchorId) addEdge(anchorId, chk.id, statusColor(undefined), { vertical: true, width: 1.5 });
      });
      continue;
    }

    // Lay this turn's pipeline nodes left → right.
    group.nodes.forEach((built, index) => {
      addNode(built, x + index * COL_GAP, y);
      const source = index === 0 ? prevNodeId : group.nodes[index - 1].id;
      if (source) {
        const stroke = built.edgeColor ?? built.color;
        // Thread branch: the first node connects from the parent turn (cross-lane).
        const isBranch = index === 0 && group.threadId != null;
        addEdge(source, built.id, isBranch ? '#8b5cf6' : stroke, { dashed: isBranch });
      }
    });

    const firstNodeId = group.nodes[0].id;
    const lastNode = group.nodes[group.nodes.length - 1];

    // Thread branch edge from the parent turn's response node down into this lane.
    if (group.threadId && group.parentStepId) {
      const parentResp = stepResponseNodeId.get(group.parentStepId);
      if (parentResp) {
        addEdge(parentResp, firstNodeId, '#8b5cf6', { vertical: true, dashed: true, width: 1.5 });
      }
    }

    // Inline check chips below the turn's response node.
    group.checks.forEach((chk, c) => {
      addNode(chk, x + (group.nodes.length - 1) * COL_GAP + c * CHECK_GAP_X, y + CHECK_OFFSET_Y, 'vertical');
      addEdge(lastNode.id, chk.id, chk.color, { vertical: true, width: 1.5 });
    });

    if (group.stepId) stepResponseNodeId.set(group.stepId, lastNode.id);
    laneLastNodeId.set(group.laneKey, lastNode.id);
    laneCursorX.set(group.laneKey, x + group.nodes.length * COL_GAP);
  }

  // Single run-outcome node at the far right; every lane's tail feeds into it.
  const maxX = Math.max(COL_GAP, ...Array.from(laneCursorX.values()));
  const midY = ((laneKeys.length - 1) * LANE_GAP) / 2;
  const outcome = outcomeNode(run, detail);
  addNode(outcome, maxX, midY);
  const outcomeStroke = statusColor(run.status);
  for (const key of laneKeys) {
    const tail = laneLastNodeId.get(key);
    if (tail && tail !== outcome.id) {
      addEdge(tail, outcome.id, outcomeStroke, { width: 1.5 });
    }
  }

  return { nodes, edges, dialogs };
}
